import { getBookableItemIdsFromTicketIds } from "services/bookableItem";
import { getEmailTemplateBynotificationTemplateId } from "../emailTemplate";
import { createTrigger } from "../trigger";
import { flattenTicketFromBookableItem } from "../bookableItem/helpers";
import {
  prisma,
  NotificationTemplate,
  EmailTemplate,
  WhiteLabel,
  Event,
  type Prisma,
  ResourceType,
  ActionType,
  NotificationTemplateType,
  NotificationType,
  TriggerEvent,
  TriggerEntityType,
  AttachmentType,
  Language,
  TemplateV2Level,
  TemplateV2Type,
  TemplateV2For,
  TriggerV2Type,
} from "client";
import { invalidateEventPageCacheForEvent } from "../event/eventPageCache";

// CREATE
export const createNotificationTemplate = async (
  userId: string,
  data: NotificationTemplate
) => {
  try {
    const template = await prisma.notificationTemplate.create({
      data: {
        ...data,
      },
    });

    if (template.eventId) {
      await invalidateEventPageCacheForEvent(template.eventId);
    }

    return { template };
  } catch (error: any) {
    console.error(error);

    if (error.code === "P2025") {
      const missingFields = error.meta.target.map((field: any) =>
        field.split(".").pop()
      );
      const errorMessage = `Missing required fields: ${missingFields.join(
        ", "
      )}. Please provide all required fields and try again.`;
      return { error: errorMessage };
    }
    return {
      error:
        "Failed to create notification template. Please check the provided data and try again.",
    };
  }
};
export const createTemplate = async (
  data: Prisma.NotificationTemplateCreateInput & {
    subject?: string;
    replyTo?: string;
    from?: string;
    jsonTemplate?: string;
    htmlTemplate?: string;
  },
  eventId: string,
  createdById: string,
  whiteLabelId: string,
  typeType?: NotificationType,
  trigger?: TriggerEvent,
  attachments?: {
    designId: string;
    type: AttachmentType;
  }[],
  invitations?: string[],
  tickets?: string[],
  floorPlanIds?: string[]
) => {
  try {
    const previousTempWithSameName =
      await prisma.notificationTemplate.findFirst({
        where: {
          name: data.name,
          eventId,
          type: data.type,
          email: {
            type:
              data.type === NotificationTemplateType.EMAIL_DESIGN
                ? typeType
                : undefined,
          },
          sms: {
            type:
              data.type === NotificationTemplateType.SMS ? typeType : undefined,
          },
          whatsapp: {
            type:
              data.type === NotificationTemplateType.WHATSAPP
                ? typeType
                : undefined,
          },
        },
      });

    if (previousTempWithSameName) {
      return {
        error: "Template with same name already exists",
      };
    }

    const { subject, replyTo, from, jsonTemplate, htmlTemplate, ...rest } =
      data;
    const bookableItemIds = tickets?.length
      ? await getBookableItemIdsFromTicketIds(tickets)
      : [];
    const template = await prisma.notificationTemplate.create({
      data: {
        ...rest,
        deletedAt: null,
        event: {
          connect: { id: eventId },
        },
        createdBy: {
          connect: { id: createdById },
        },
        whiteLabel: {
          connect: { id: whiteLabelId },
        },
        invitations: invitations?.length
          ? {
              connect: invitations?.map((id) => ({ id })),
            }
          : {},
        bookableItems: bookableItemIds?.length
          ? {
              connect: bookableItemIds?.map((id) => ({ id })),
            }
          : {},

        floorPlans: floorPlanIds?.length
          ? {
              connect: floorPlanIds?.map((id) => ({ id })),
            }
          : {},
        ...(typeType && data.type === NotificationTemplateType.EMAIL_DESIGN
          ? {
              email: {
                create: {
                  type: typeType,
                  jsonTemplate: jsonTemplate ?? "",
                  htmlTemplate: JSON.stringify(htmlTemplate) ?? "",
                  subject: subject ?? data.name,
                  replyTo: replyTo,
                  from: from,
                },
              },
            }
          : data.type === NotificationTemplateType.SMS
          ? {
              sms: {
                create: {
                  type: typeType,
                  message: "",
                },
              },
            }
          : {}),
        ...(attachments?.length
          ? {
              attachments: {
                createMany: {
                  data: attachments.map((attachment) => ({
                    type: attachment.type,
                    designId: attachment.designId, // Use the ID directly
                  })),
                },
              },
            }
          : {}),
      },
    });

    if (trigger) {
      await createTrigger(
        {
          triggerEvent: trigger,

          createdBy: {
            connect: { id: createdById },
          },
          createdAt: new Date(),
          updatedAt: new Date(),
        } as Prisma.TriggerCreateInput,
        TriggerEntityType.NOTIFICATION,
        template.id,
        eventId
      );
    }

    return template;
  } catch (error: any) {
    console.error(error);
    throw new Error(error);
  }
};
// READ
export const getAllNotificationTemplates = async (
  userId: string
): Promise<{
  templates?: NotificationTemplate[];
  error?: any;
}> => {
  try {
    const templates = await prisma.notificationTemplate.findMany({
      where: {
        deletedAt: null,
      },
    });
    return { templates };
  } catch (error) {
    return { error };
  }
};

export const getAllNotificationTemplatesWithCreatedBy = async (
  userId: string
): Promise<{
  templates?: NotificationTemplate[];
  error?: any;
}> => {
  try {
    const templates = await prisma.notificationTemplate.findMany({
      where: {
        deletedAt: null,
      },
      include: { createdBy: true },
    });
    return { templates };
  } catch (error) {
    return { error };
  }
};
export const getNotificationTemplateById = async (
  userId: string,
  id: string
) => {
  try {
    const template = await prisma.notificationTemplate.findUnique({
      where: { id },
      include: {
        bookableItems: {
          include: {
            translations: true,
            ticket: true,
          },
        },
        floorPlans: {
          where: {
            deletedAt: null,
          },
        },
        invitations: { include: { translations: true } },
        whatsapp: true,
        email: true,
        sms: true,
        attachments: true,
        triggers: {
          include: {
            trigger: true,
          },
        },
      },
    });
    if (!template) return null;
    return {
      template: {
        ...template,
        tickets:
          template?.bookableItems
            ?.filter((bookableItem) => bookableItem != null)
            ?.map((bookableItem) => flattenTicketFromBookableItem(bookableItem))
            .filter(Boolean) || [],
      },
    };
  } catch (error) {
    return { error };
  }
};
export const getNotificationTemplateByIdPublic = async (id: string) => {
  try {
    const template = await prisma.notificationTemplate.findUnique({
      where: { id },
      include: {
        bookableItems: {
          include: {
            translations: true,
            ticket: true,
          },
        },
        invitations: { include: { translations: true } },
        whatsapp: true,
        email: true,
        sms: true,
      },
    });
    return {
      template: {
        ...template,
        tickets: template?.bookableItems
          ?.filter((bookableItem) => bookableItem != null)
          ?.map(flattenTicketFromBookableItem)
          .filter(Boolean),
      },
    };
  } catch (error) {
    return { error };
  }
};
type NotificationTemplateWithRelations = Prisma.NotificationTemplateGetPayload<{
  include: {
    email: true;
    sms: true;
    whatsapp: true;
    event: {
      include: {
        organizations: {
          select: {
            id: true;
          };
        };
        translations: {
          include: {
            language: true;
          };
        };
      };
    };
    whiteLabel: true;
    attachments: {
      include: {
        design: {
          include: {
            translations: true;
          };
        };
      };
    };
  };
}>;

export const getNotificationTemplateByIdWithAllTemplates = async (
  id: string
): Promise<{
  template?: NotificationTemplateWithRelations | null;
  error?: any;
}> => {
  try {
    const template = await prisma.notificationTemplate.findUnique({
      where: { id },
      include: {
        email: true,
        sms: true,
        whatsapp: true,
        event: {
          include: {
            organizations: {
              select: {
                id: true,
              },
            },
            translations: {
              include: {
                language: true,
              },
            },
          },
        },
        whiteLabel: true,
        attachments: {
          include: {
            design: {
              include: {
                translations: true,
              },
            },
          },
        },
      },
    });

    return {
      template: template as unknown as NotificationTemplateWithRelations,
    };
  } catch (error) {
    return { error };
  }
};
export const getNotificationTemplateByIdwithWhiteLabelAndEventData = async (
  userId: string,
  id: string
): Promise<{
  template?: NotificationTemplate & {
    whiteLabel?: WhiteLabel;
    event?: Event;
  };
  error?: any;
}> => {
  try {
    const template = (await prisma.notificationTemplate.findUnique({
      where: { id },
      include: {
        whiteLabel: true,
        event: true,
      },
    })) as NotificationTemplate;
    return { template };
  } catch (error) {
    return { error };
  }
};
export const getNotificationTemplateByIdwithWhiteLabelAndEventDataPublic =
  async (
    id: string
  ): Promise<{
    template?: NotificationTemplate & {
      whiteLabel?: WhiteLabel;
      event?: Event;
    };
    error?: any;
  }> => {
    try {
      const template = (await prisma.notificationTemplate.findUnique({
        where: { id },
        include: {
          whiteLabel: true,
          event: true,
        },
      })) as NotificationTemplate;
      return { template };
    } catch (error) {
      return { error };
    }
  };
export const getInvitationsNotificationTemplates = async (
  userId: string,
  eventId: string
) => {
  try {
    const template = await prisma.notificationTemplate.findMany({
      where: {
        eventId,
        deletedAt: null,
        OR: [
          { email: { type: NotificationType.INVITATION } },
          { sms: { type: NotificationType.INVITATION } },
          { whatsapp: { type: NotificationType.INVITATION } },
        ],
      },
      include: {
        whiteLabel: true,
        event: true,
      },
    });
    return template;
  } catch (e: any) {
    console.error(e);
    throw new Error(e.message);
  }
};

export const getNotificationTemplatesByEventId = async (
  userId: string,
  eventId: string,
  invitationId?: string
): Promise<{
  templates?: NotificationTemplate[];
  emailTemplates?: EmailTemplate[];
  error?: any;
}> => {
  try {
    let whereClause: any = {
      eventId,
      deletedAt: null,
    };
    if (invitationId) {
      whereClause.invitations = {
        some: {
          id: invitationId,
        },
      };
    }
    const templates = await prisma.notificationTemplate.findMany({
      where: whereClause,
      include: {
        email: true,
        sms: true,
        whatsapp: true,
      },
    });

    let emailTemplates: EmailTemplate[] = [];

    if (templates) {
      for (const template of templates) {
        const { emailTemplate, error } =
          await getEmailTemplateBynotificationTemplateId(userId, template.id);
        if (error) throw new Error(error as string);
        // @ts-ignore
        template.emailTemplates = emailTemplate;

        emailTemplates.push(emailTemplate as EmailTemplate); // Store the email template in the emailTemplates array
      }
    }

    return { templates, emailTemplates };
  } catch (error) {
    console.error(error);
    return { error };
  }
};

export const getNotificationTemplatesByWhiteLabelId = async (
  userId: string,
  whiteLabelId: string
): Promise<{
  templates?: NotificationTemplate[];
  error?: any;
}> => {
  try {
    const templates = await prisma.notificationTemplate.findMany({
      where: { whiteLabelId, deletedAt: null },
      include: { createdBy: true },
    });

    return { templates };
  } catch (error) {
    return { error };
  }
};

// UPDATE
export const updateNotificationTemplateById = async (
  userId: string,
  data: Prisma.NotificationTemplateCreateInput & {
    subject?: string;
    replyTo?: string;
    from?: string;
  },
  typeType?: NotificationType,
  trigger?: TriggerEvent,
  attachments?: {
    designId: string;
    type: AttachmentType;
    repeatForEachTicket?: boolean;
  }[],
  invitations?: string[],
  tickets?: string[],
  floorPlanIds?: string[]
) => {
  try {
    const { id, subject, replyTo, from, type, ...rest } = data;
    // disconnect all tickets and invitations old
    const currentTemplate = await prisma.notificationTemplate.findUnique({
      where: { id },
      include: {
        invitations: { select: { id: true } },
        bookableItems: { select: { id: true, ticketId: true } },
        floorPlans: {
          select: { id: true },
          where: {
            deletedAt: null,
          },
        },
      },
    });

    const currentInvitations = currentTemplate?.invitations || [];

    const currentBookableItems = currentTemplate?.bookableItems || [];

    const currentFloorPlans = currentTemplate?.floorPlans || [];

    // Convert ticket IDs to bookableItem IDs
    const bookableItemIds = tickets?.length
      ? await getBookableItemIdsFromTicketIds(tickets)
      : [];
    const emailData = {
      type: typeType,
      subject: subject ?? data.name,
      replyTo: replyTo,
      from: from,
    };
    const template = await prisma.notificationTemplate.update({
      where: { id },
      data: {
        ...rest,
        ...(typeType && data.type === NotificationTemplateType.EMAIL_DESIGN
          ? {
              email: {
                upsert: {
                  update: emailData,
                  create: {
                    ...emailData,
                    htmlTemplate: "",
                    jsonTemplate: "",
                    mergeTags: [],
                  },
                },
              },
            }
          : {}),
        ...(attachments?.length
          ? {
              attachments: {
                deleteMany: {},
                createMany: {
                  data: attachments.map((attachment) => ({
                    type: attachment.type,
                    designId: attachment.designId,
                  })),
                },
              },
            }
          : {}),
        invitations: {
          disconnect: currentInvitations
            .filter((inv: any) => !invitations?.includes(inv.id)) // Find unselected invitations
            .map((inv: any) => ({ id: inv.id })),
          connect: invitations?.map((id) => ({ id })), // Connect selected invitations
        },
        bookableItems: {
          disconnect:
            currentBookableItems
              ?.filter(
                (item: any) => item?.id && !bookableItemIds?.includes(item.id)
              ) // Find unselected bookable items
              ?.map((item: any) => ({ id: item.id })) || [],
          connect: bookableItemIds?.map((id) => ({ id })), // Connect selected bookable items
        },
        floorPlans: {
          disconnect: currentFloorPlans
            .filter((floorPlan: any) => !floorPlanIds?.includes(floorPlan.id)) // Find unselected floor plans
            .map((floorPlan: any) => ({ id: floorPlan.id })),
          connect: floorPlanIds?.map((id) => ({ id })), // Connect selected floor plans
        },

        updatedAt: new Date(),
      },
    });

    if (trigger) {
      const triggers = await prisma.trigger.deleteMany({
        where: {
          triggerEntity: {
            entityId: id,
          },
        },
      });
      await prisma.triggerEntity.deleteMany({
        where: {
          entityId: id,
        },
      });

      await createTrigger(
        {
          triggerEvent: trigger,
          createdBy: {
            connect: { id: userId },
          },
        } as Prisma.TriggerCreateInput,
        TriggerEntityType.NOTIFICATION,
        template.id,
        template.eventId
      );
    }

    if (template.eventId) {
      await invalidateEventPageCacheForEvent(template.eventId);
    }

    return template;
  } catch (error: any) {
    console.error(error);
    throw new Error(error);
  }
};

export const LinkNotificationTemplateWithTicketsAndInvitationsIdAndFloorPlanId =
  async (
    userId: string,
    id: string,
    ticketIds: string[],
    invitationIds: string[],
    floorPlanIds: string[]
  ) => {
    try {
      const template = await prisma.notificationTemplate.findUnique({
        where: { id },
        include: {
          bookableItems: {
            select: {
              id: true,
              ticketId: true,
            },
          },
          invitations: true,
          floorPlans: {
            where: {
              deletedAt: null,
            },
          },
        },
      });
      if (!template) throw new Error(`template with ID ${id} not found`);
      const bookableItemIds = ticketIds.length
        ? await getBookableItemIdsFromTicketIds(ticketIds)
        : [];
      let bookableItemsToDisconnect: string[] = [],
        invitationsToDisconnect: string[] = [];
      let floorPlansToDisconnect: string[] = [];

      if (template?.bookableItems?.length)
        bookableItemsToDisconnect = template.bookableItems
          .filter((item) => item.id && !ticketIds.includes(item.id))
          .map((item) => item.id)
          .filter((item) => item !== null);

      if (template?.invitations?.length)
        invitationsToDisconnect = template.invitations
          .filter((invitation) => !invitationIds.includes(invitation.id))
          .map((ticket) => ticket.id);
      if (template?.floorPlans?.length)
        floorPlansToDisconnect = template.floorPlans
          .filter((floorPlan) => !floorPlanIds.includes(floorPlan.id))
          .map((floorPlan) => floorPlan.id);
      const response = await prisma.notificationTemplate.update({
        where: { id },
        data: {
          invitations: {
            connect: invitationIds.map((id) => ({ id })),
            disconnect: invitationsToDisconnect?.map((id) => ({
              id,
            })),
          },
          bookableItems: {
            connect: bookableItemIds.map((id) => ({ id })),
            disconnect: bookableItemsToDisconnect?.map((id) => ({
              id,
            })),
          },
          floorPlans: {
            connect: floorPlanIds.map((id) => ({ id })),
            disconnect: floorPlansToDisconnect?.map((id) => ({
              id,
            })),
          },
        },
      });
      return response;
    } catch (e: any) {
      console.error(e);
      throw new Error(e);
    }
  };

// DELETE
export const deleteNotificationTemplateById = async (
  userId: string,
  id: string
) => {
  try {
    await prisma.emailTemplate.deleteMany({
      where: { notificationTemplateId: id },
    });

    await prisma.sMS.deleteMany({
      where: { notificationTemplateId: id },
    });

    await prisma.whatsappFormTemplate.deleteMany({
      where: { notificationTemplateId: id },
    });

    await prisma.trigger.deleteMany({
      where: {
        triggerEntity: {
          entityId: id,
        },
      },
    });
    await prisma.triggerEntity.deleteMany({
      where: {
        entityId: id,
      },
    });
    await prisma.attachment.deleteMany({
      where: {
        notificationTemplateId: id,
      },
    });
    return await prisma.notificationTemplate.delete({
      where: { id },
    });
  } catch (error: any) {
    throw new Error(error);
  }
};

export const createAttachments = async (
  attachments: {
    designId: string;
    type: AttachmentType;
    repeatForEachTOrderItem?: boolean;
  }[],
  notificationTemplateId?: string,
  defaultTemplateId?: string
) => {
  // disconnect all attachments old
  if (notificationTemplateId) {
    await prisma.attachment.deleteMany({
      where: {
        notificationTemplateId,
      },
    });
  }
  if (defaultTemplateId) {
    await prisma.attachment.deleteMany({
      where: {
        defaultTemplateId,
      },
    });
  }
  return await prisma.attachment.createMany({
    data: attachments.map((attachment) => ({
      type: attachment.type,
      designId: notificationTemplateId ? attachment.designId : null,
      defaultTemplateAttachmentId: defaultTemplateId
        ? attachment.designId
        : null,
      notificationTemplateId: notificationTemplateId || null,
      defaultTemplateId: defaultTemplateId || null,
      repeatForEachTOrderItem: attachment.repeatForEachTOrderItem || false,
    })),
  });
};

const INVITATION_COMMUNICATION_TRIGGERS: TriggerV2Type[] = [
  TriggerV2Type.ON_INVITATION_SENT,
  TriggerV2Type.ON_INVITATION_REMINDER,
  TriggerV2Type.ON_INVITATION_ACCEPTED,
  TriggerV2Type.ON_INVITATION_MAYBE,
  TriggerV2Type.ON_INVITATION_REJECTED,
];

const toInvitationTemplateGroupType = (type: TemplateV2Type) => {
  if (type === TemplateV2Type.EMAIL) return "EMAIL_DESIGN";
  return type;
};

export const getInvitationsTemplatesByEventId = async (eventId: string) => {
  try {
    const [communicationTemplates, physicalTemplates] = await Promise.all([
      prisma.templateV2.findMany({
        where: {
          level: TemplateV2Level.EVENT,
          scopeId: eventId,
          templateFor: TemplateV2For.INVITATION_COMMUNICATION,
          type: {
            in: [
              TemplateV2Type.EMAIL,
              TemplateV2Type.SMS,
              TemplateV2Type.WHATSAPP,
            ],
          },
          trigger: { in: INVITATION_COMMUNICATION_TRIGGERS },
          deletedAt: null,
        },
        orderBy: [{ updatedAt: "desc" }, { createdAt: "desc" }],
      }),
      prisma.templateV2.findMany({
        where: {
          level: TemplateV2Level.EVENT,
          scopeId: eventId,
          type: TemplateV2Type.PDF,
          templateFor: TemplateV2For.PRINTABLE_INVITATION_PDF,
          deletedAt: null,
        },
        orderBy: [{ updatedAt: "desc" }, { createdAt: "desc" }],
      }),
    ]);

    const groupedCommunication = communicationTemplates.reduce<
      { type: string; templates: typeof communicationTemplates }[]
    >((groups, template) => {
      const groupType = toInvitationTemplateGroupType(template.type);
      const existingGroup = groups.find((group) => group.type === groupType);

      if (existingGroup) {
        existingGroup.templates.push(template);
        return groups;
      }

      groups.push({ type: groupType, templates: [template] });
      return groups;
    }, []);

    if (!physicalTemplates.length) {
      return groupedCommunication;
    }

    return [
      ...groupedCommunication,
      {
        type: "PHYSICAL",
        templates: physicalTemplates.map((template) => ({
          id: template.id,
          type: "PHYSICAL",
          name: template.name,
          templates: {
            design: {
              name: template.name,
            },
          },
        })),
      },
    ];
  } catch (e: any) {
    console.error(e);
    throw new Error(e);
  }
};

export const softDeleteNotificationTemplate = async (
  userId: string,
  id: string
) => {
  try {
    await deleteNotificationTemplateById(userId, id);
  } catch (e) {
    return e;
  }
};

export const duplicateNotificationTemplate = async (
  userId: string,
  id: string
) => {
  try {
    const template = await prisma.notificationTemplate.findUnique({
      where: { id },
      include: {
        email: true,
        sms: true,
        whatsapp: true,
        attachments: true,
        triggers: {
          include: {
            trigger: true,
          },
        },
      },
    });
    if (!template) {
      throw new Error("Template not found");
    }

    const newTemplate = await createTemplate(
      {
        name: `${template.name} (Copy)`,
        type: template.type,
        subject: template.email?.subject ?? undefined,
        replyTo: template.email?.replyTo ?? undefined,
        from: template.email?.from ?? undefined,
        jsonTemplate: template.email?.jsonTemplate ?? undefined,
        htmlTemplate: template.email?.htmlTemplate ?? undefined,
      } as any,
      template.eventId ?? "",
      userId,
      template.whiteLabelId ?? "",
      template.type === NotificationTemplateType.EMAIL_DESIGN
        ? template.email?.type ?? undefined
        : undefined,
      template.triggers?.[0]?.trigger?.triggerEvent,
      template.attachments
        ?.filter((a) => a.designId !== null)
        .map((a) => ({ designId: a.designId!, type: a.type })),
      [],
      [],
      []
    );
    console.log("newTemplate", newTemplate);
    return newTemplate;
  } catch (e) {
    throw e;
  }
};
